import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  IconButton,
} from "@mui/material";
import {
  PlayArrow,
  CheckCircle,
  Phone,
  CalendarToday,
} from "@mui/icons-material";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../auth/useAuthContext";

export default function HeroSection() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  return (
    <Box
      sx={{
        background:
          'linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url("https://readdy.ai/api/search-image?query=Modern%20medical%20optometry%20clinic%20office%20with%20professional%20lighting%2C%20clean%20white%20interior%20design%2C%20advanced%20eye%20examination%20equipment%2C%20professional%20healthcare%20environment%2C%20minimalist%20design%20with%20blue%20accent%20colors%2C%20welcoming%20patient%20care%20atmosphere%2C%20contemporary%20medical%20facility%20with%20natural%20lighting%20and%20clean%20surfaces&width=1920&height=1080&seq=hero-bg-001&orientation=landscape")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        color: "white",
        position: "relative",
      }}>
      <Container
        maxWidth="lg"
        sx={{
          pt: 6,
          pb: 8,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            {/* Urgency Banner - Moved inside content area */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "#dc2626",
                color: "white",
                px: 2,
                py: 1,
                borderRadius: "9999px",
                fontSize: "14px",
                fontWeight: 600,
                mb: 3,
                gap: 1,
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}>
              <Box
                component="i"
                sx={{
                  fontSize: "16px",
                  "&::before": {
                    content: '"🔥"',
                  },
                }}
              />
              Only 8 SPOTS LEFT for Our Fully-Managed Onboarding! Secure Your
              Practice's Future Today.
            </Box>

            <Typography
              variant="h1"
              sx={{
                mb: 3,
                fontSize: { xs: "2.25rem", lg: "3rem" }, // text-4xl lg:text-5xl
                fontWeight: 700, // font-bold
                lineHeight: 1.1, // leading-tight
                "& .highlight": {
                  color: "#60a5fa", // text-blue-400
                },
              }}>
              Exclusive Onboarding for Just 10 Practices!
              <br />
              <span className="highlight">
                Built By an Optician, For Opticians.
              </span>
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  backgroundColor: "#15803d",
                  px: 4,
                  py: 3,
                  fontWeight: 600,
                  fontSize: "18px",
                  textTransform: "none",
                  transform: "scale(1)",
                  transition: "all 0.3s ease",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#059669",
                    transform: "scale(1.02)",
                  },
                }}
                startIcon={<PlayArrow />}
                onClick={() =>
                  navigate(isAuthenticated ? "/patients/list" : "/auth/login")
                }>
                Live Demo - No Sign Up Required
              </Button>
            </Box>

            {/* Trust Indicators */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                flexWrap: "wrap",
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle sx={{ color: "#4ade80", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", color: "#d1d5db" }}>
                  HIPAA Compliant
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle sx={{ color: "#4ade80", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", color: "#d1d5db" }}>
                  GDPR Compliant
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", color: "#d1d5db" }}>
                  ⭐ 4.9/5 Rating
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.875rem", color: "#d1d5db" }}>
                👥 150+ Practices
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ pl: { lg: 4 } }}>
              {/* Video Container */}
              <Paper
                sx={{
                  position: "relative",
                  backgroundColor: "#1e293b",
                  borderRadius: 2,
                  p: 6,
                  backgroundImage:
                    'url("https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=800")',
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  aspectRatio: "16/9",
                  transform: "scale(1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    borderRadius: 2,
                  }}
                />
                <IconButton
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                    backgroundColor: "#2563eb",
                    color: "white",
                    width: 70,
                    height: 70,
                    "&:hover": {
                      backgroundColor: "#1d4ed8",
                      transform: "translate(-50%, -50%) scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                  }}>
                  <PlayArrow sx={{ fontSize: 35 }} />
                </IconButton>
              </Paper>

              {/* Viewer Count - Outside video container with backdrop blur */}
              <Box
                sx={{
                  mt: 3,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(8px)",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  color: "white",
                }}>
                <Typography variant="body2" sx={{ fontSize: "14px" }}>
                  👁️ Over 2,500 optometrists have watched this demo
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Scroll Indicator */}
      <Box
        sx={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          color: "white",
          animation: "bounce 2s infinite",
          "@keyframes bounce": {
            "0%, 20%, 53%, 80%, 100%": {
              transform: "translateX(-50%) translateY(0)",
            },
            "40%, 43%": {
              transform: "translateX(-50%) translateY(-10px)",
            },
            "70%": {
              transform: "translateX(-50%) translateY(-5px)",
            },
            "90%": {
              transform: "translateX(-50%) translateY(-2px)",
            },
          },
        }}>
        <Typography sx={{ fontSize: "24px" }}>↓</Typography>
      </Box>
    </Box>
  );
}
